/**
 * @fileoverview "Plant something" (E25; Fable's mock-up v2 with the owner's changes of 30 Oct): one sheet, in her
 * order, that adds a plant Grow knows at Planning and its to-do "Plant <name>" on the day she says, together.
 *
 * - Which plant? The search box, focused as the sheet opens, and under it the server's rows as sent (B121: GET
 *   /plants/search; her own kinds marked "(yours)"), or "No plant called … in Grow yet"; a quiet line says any plant
 *   Grow knows can be picked, in season or not. No Describe it here (owner, 30 Oct: it stays on the Plants page's
 *   form). Picked, the plant is a card: its name (the server's), the row's detail, and Change.
 * - When will you plant it? Today, This week, Pick a day (WhenChips: one row, never wrapping). Once a plant is picked,
 *   the server's "Grow recommends: …" sits under the question; when its days open later the sheet has picked Pick a
 *   day with Grow's best day in the box, and the line reads "Which day? Grow recommends: …" over it. No dates: no line.
 * - Which bed? Always shown (owner, 30 Oct): the bed she came from, else her only bed, else "Pick a bed".
 * - Already there (E26; owner, 30 Oct): when the bed chosen already has a plant of that kind, the server's line ("You
 *   already have Romaine Lettuce in Bed 1 (planning since Oct 30).") and its two choices, "Use that one" and "Add
 *   another", the server's default chosen (AlreadyChoice), under Which bed?.
 * - The check-mark line, the server's: "Romaine Lettuce will be on your Plants list in planning stage."; while "Use that
 *   one" is chosen, its own ("The to-do goes on your Romaine Lettuce in Bed 1.").
 * - The foot (rule 7): Cancel on the left, Add on the right; Add waits for a plant, a bed, the plan and a day. Until
 *   then it is grey and aria-disabled, never the `disabled` attribute, so a tap is answered (B133): with no bed chosen
 *   the tap marks the bed select invalid, moves focus to it and shows "Choose a bed." under "Which bed?" (the Add a plant
 *   form's line); any other missing piece is silent.
 *
 * The app decides nothing: the rows, the name, the line, the day the sheet starts on, the to-do's title and the
 * toast are the server's (usePlantSomething).
 *
 * Where: useOwnTodoSheet (Today's and a bed's "Plant something" button).
 * API: through usePlantSomething.
 * Words (en/es): todo.own.plant.button (the title), .whichPlant, .placeholder, .hint, .change, .when, .whichBed;
 * todo.own.whichDay, todo.own.addButton; plants.form.fields.yours, plants.form.pickLocation, plants.form.missing.location, sitewide.saving (Button's loading word), sitewide.cancel (ActionRow);
 * plants.form.fields.noMatch (NoPlantFound). Feet (B111): ActionRow and Button at 44px; Button `loading` while it saves.
 *
 * @module own-todos/PlantSomethingSheet
 */

import React from 'react';
import { Check, Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import { NoPlantFound, PlantSearchField } from '@/shared/components/plant-fields/PlantSearchField';
import { WhenChips } from './WhenChips';
import { AlreadyChoice } from './AlreadyChoice';
import type { PlantSomething } from './usePlantSomething';

/** The sheet's props. */
interface PlantSomethingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The garden's day, "YYYY-MM-DD": the first day Pick a day offers. */
  today: string;
  /** Its state and actions (usePlantSomething). */
  sheet: PlantSomething;
}

/** A field's question over it. */
const QUESTION = 'mb-2 block text-sm font-semibold text-foreground';

/**
 * The "Plant something" sheet.
 *
 * @param props - PlantSomethingSheetProps.
 */
export const PlantSomethingSheet: React.FC<PlantSomethingSheetProps> = ({ open, onOpenChange, today, sheet }) => {
  const { reactTranslate: t } = useTranslation();
  const { picked, plan, search } = sheet;
  /** The bed select, for the focus a tap on Add with no bed moves to it. */
  const bedRef = React.useRef<HTMLSelectElement>(null);
  const rows = search.data?.rows ?? [];
  // The check-mark line: the chosen choice's while the bed already has one (E26), else the plan's.
  const onList = plan?.already?.choices.find((choice) => choice.key === sheet.already)?.onList ?? plan?.onList;

  /**
   * Adds, and closes once saved. With no bed chosen the tap sends nothing and focus goes to the bed select (B133).
   *
   * @param event - The form's submit.
   */
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (await sheet.save()) onOpenChange(false);
    else if (sheet.locationId === null) bedRef.current?.focus();
  };

  return (
    <BottomPanel open={open} onOpenChange={onOpenChange} title={t('todo.own.plant.button')} wrapTitle>
      <form onSubmit={(event) => { void submit(event); }} className="space-y-4">
        <div>
          <span id="plant-something-which" className={QUESTION}>{t('todo.own.plant.whichPlant')}</span>
          {picked ? (
            <div className="flex min-h-14 items-center gap-3 rounded-md border border-primary bg-primary/10 px-3 py-2">
              <Sprout className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold leading-snug text-foreground">{plan?.title ?? picked.label}</span>
                {picked.detail && <span className="block text-sm leading-snug text-muted-foreground">{picked.detail}</span>}
              </span>
              <button
                type="button"
                onClick={sheet.change}
                disabled={sheet.busy}
                className="min-h-11 shrink-0 px-1 font-semibold text-primary underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('todo.own.plant.change')}
              </button>
            </div>
          ) : (
            <>
              <PlantSearchField
                idPrefix="plant-something"
                query={sheet.query}
                onQueryChange={sheet.setQuery}
                options={rows.map((row) => ({ key: row.key, label: row.own ? `${row.label} ${t('plants.form.fields.yours')}` : row.label, detail: row.detail }))}
                onChoose={(key) => { const row = rows.find((one) => one.key === key); if (row) sheet.pick(row); }}
                placeholder={t('todo.own.plant.placeholder')}
                disabled={sheet.busy}
                autoFocus
                // "Nothing found" only once the server has answered what is in the box; its failure says so instead.
                empty={search.error ? <ServerFailureNote error={search.error} where="Plant something search" /> : search.current && <NoPlantFound query={sheet.query} />}
              />
              <p className="mt-1.5 text-sm text-muted-foreground">{t('todo.own.plant.hint')}</p>
            </>
          )}
          {sheet.planError ? <ServerFailureNote error={sheet.planError} where="Plant something plan" className="mt-2 text-sm text-destructive" /> : null}
        </div>

        <div>
          <p id="plant-something-when" className="mb-2 text-sm font-semibold text-foreground">{t('todo.own.plant.when')}</p>
          {plan?.recommends && sheet.when !== 'day' && <p className="-mt-1 mb-2 text-sm text-muted-foreground">{plan.recommends}</p>}
          <WhenChips value={sheet.when} onChange={sheet.pickWhen} labelledBy="plant-something-when" />
          {sheet.when === 'day' && (
            <label className="mt-3 block">
              <span className="mb-1 block text-sm">
                <span className="font-semibold text-foreground">{t('todo.own.whichDay')}</span>
                {plan?.recommends && <span className="text-muted-foreground"> {plan.recommends}</span>}
              </span>
              <input
                type="date"
                min={today}
                value={sheet.day}
                onChange={(event) => sheet.setDay(event.target.value)}
                className="h-12 w-full rounded-md border border-input bg-card px-3 text-base text-foreground focus:border-primary focus:outline-none"
              />
            </label>
          )}
        </div>

        <div>
          <label htmlFor="plant-something-bed" className={QUESTION}>{t('todo.own.plant.whichBed')}</label>
          <NativeSelect
            ref={bedRef}
            id="plant-something-bed"
            value={sheet.locationId ?? ''}
            onChange={(event) => sheet.setLocationId(event.target.value ? Number(event.target.value) : null)}
            disabled={sheet.busy}
            invalid={sheet.bedMissing}
            aria-describedby={sheet.bedMissing ? 'plant-something-bed-missing' : undefined}
          >
            {sheet.locationId === null && <option value="">{t('plants.form.pickLocation')}</option>}
            {sheet.beds.map((bed) => <option key={bed.id} value={bed.id}>{bed.name}</option>)}
          </NativeSelect>
          {sheet.bedMissing && <p id="plant-something-bed-missing" className="mt-1 text-sm font-medium text-destructive">{t('plants.form.missing.location')}</p>}
        </div>

        {plan?.already && <AlreadyChoice already={plan.already} value={sheet.already} onChange={sheet.pickAlready} disabled={sheet.busy} />}

        {plan && onList && (
          <p className="flex items-start gap-2 text-[0.9375rem] leading-snug text-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{onList}</span>
          </p>
        )}

        <div className="space-y-2">
          <ActionRow onCancel={() => onOpenChange(false)} disabled={sheet.busy}>
            {/* aria-disabled, never disabled, while not ready: a tap with no bed is answered "Choose a bed." (B133). */}
            <Button type="submit" aria-disabled={!sheet.ready} loading={sheet.busy} className={sheet.ready ? undefined : 'opacity-50'}>
              {t('todo.own.addButton')}
            </Button>
          </ActionRow>
          {sheet.failed && <p className="text-sm text-destructive" role="alert"><FailureText text={sheet.failed} /></p>}
        </div>
      </form>
    </BottomPanel>
  );
};
